import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  KeyRound,
  Sparkles,
  Store,
  Key,
  CheckCircle2,
  ArrowRight,
  Rocket,
  PartyPopper,
  ChevronRight,
  HelpCircle,
  BarChart3,
  Activity,
  Shield,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

const STEPS = [
  {
    id: "api_key",
    number: 1,
    icon: KeyRound,
    color: "#F59E0B",
    bg: "bg-amber-50",
    border: "border-amber-200",
    title: "Add your first API key",
    simple: "This is like putting batteries in a toy. Without a key, nothing works.",
    detail: "Go to API Keys, pick a provider (like OpenAI), paste your secret key. That's it. The app encrypts and stores it safely.",
    route: "/keys",
    buttonLabel: "Add API Key",
    doneLabel: "key added",
  },
  {
    id: "playground",
    number: 2,
    icon: Sparkles,
    color: "#8B5CF6",
    bg: "bg-violet-50",
    border: "border-violet-200",
    title: "Try the Playground",
    simple: "Type something. Pick a model. Hit Send. See the AI reply. That's the whole thing.",
    detail: "The Playground lets you test any model directly. Pick a provider & model from the dropdowns, type a message, and press Send. You'll see the response, token count, and cost.",
    route: "/playground",
    buttonLabel: "Open Playground",
    doneLabel: "used",
  },
  {
    id: "plugin",
    number: 3,
    icon: Store,
    color: "#EC4899",
    bg: "bg-pink-50",
    border: "border-pink-200",
    title: "Install a provider from Marketplace",
    simple: "Want more AI models? One click to add DeepSeek, Mistral, and others.",
    detail: "Go to Plugins > Marketplace. You'll see popular providers. Click 'Install' on any. Then go to API Keys to add that provider's API key. Done. New models are now available everywhere.",
    route: "/plugins",
    buttonLabel: "Browse Marketplace",
    doneLabel: "installed",
  },
  {
    id: "hub_key",
    number: 4,
    icon: Key,
    color: "#06B6D4",
    bg: "bg-cyan-50",
    border: "border-cyan-200",
    title: "Create a Hub API Key",
    simple: "Issue an external API key so any app can use your unified endpoint.",
    detail: "Go to API Management, click 'Create Key'. Give it a name, top up the INR wallet, and use the key in any OpenAI-compatible client. All requests route through your Hub with transparent failover.",
    route: "/api-management",
    buttonLabel: "Create Hub Key",
    doneLabel: "key created",
  },
];

const BONUS_TIPS = [
  {
    icon: BarChart3,
    title: "Track Spending",
    desc: "Dashboard shows total spend per provider. Usage History has daily/weekly/monthly breakdowns with CSV export.",
    route: "/usage",
  },
  {
    icon: Activity,
    title: "Live Monitor",
    desc: "Watch API calls happen in real-time. See tokens flowing, costs accumulating, which models are being used.",
    route: "/monitor",
  },
  {
    icon: Shield,
    title: "Unified API Endpoint",
    desc: "Use the /v1/chat/completions endpoint as a drop-in replacement for OpenAI in any app. Auto-failover included.",
    route: "/api-management",
  },
];

export default function GettingStartedPage({ api, onNavigate }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${api}/onboarding/status`);
      setStatus(res.data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const stepStatus = (id) => {
    if (!status) return { done: false, count: 0 };
    const s = status.steps.find(s => s.id === id);
    return s || { done: false, count: 0 };
  };

  const completedCount = status?.completed || 0;
  const totalCount = status?.total || STEPS.length;
  const progressPct = Math.round((completedCount / totalCount) * 100);
  const allDone = status?.all_done || false;

  return (
    <div className="main-content-inner max-w-3xl mx-auto" data-testid="getting-started-page">

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
          {allDone ? (
            <PartyPopper className="w-8 h-8 text-primary" />
          ) : (
            <Rocket className="w-8 h-8 text-primary" />
          )}
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
          {allDone ? "You're all set!" : "Getting Started"}
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
          {allDone
            ? "You've completed all the steps. Your API Gateway is fully configured."
            : "Follow these 4 simple steps to set up your API Gateway. Each one takes under a minute."}
        </p>
      </motion.div>

      {/* Progress bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-muted-foreground">Progress</span>
          <span className="text-xs font-semibold">{completedCount} of {totalCount} done</span>
        </div>
        <Progress value={progressPct} className="h-2.5" />
      </motion.div>

      {/* Steps */}
      <div className="space-y-4 mb-10">
        {STEPS.map((step, i) => {
          const st = stepStatus(step.id);
          const done = st.done;

          return (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * i + 0.3 }}
            >
              <Card
                className={`transition-all ${done ? "opacity-80" : "hover:shadow-md"} ${step.border}`}
                style={{ borderLeftWidth: 4, borderLeftColor: step.color }}
                data-testid={`onboarding-step-${step.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 mt-0.5">
                      {done ? (
                        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        </div>
                      ) : (
                        <div className={`w-10 h-10 rounded-xl ${step.bg} flex items-center justify-center`}>
                          <step.icon className="w-5 h-5" style={{ color: step.color }} />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge
                          variant="outline"
                          className="text-[10px] h-5 font-mono tabular-nums"
                          style={{ borderColor: step.color, color: step.color }}
                        >
                          Step {step.number}
                        </Badge>
                        {done && (
                          <Badge className="text-[10px] h-5 bg-emerald-100 text-emerald-700 border-emerald-200 gap-0.5">
                            <CheckCircle2 className="w-2.5 h-2.5" />
                            {st.count > 1 ? `${st.count} ${step.doneLabel}` : step.doneLabel}
                          </Badge>
                        )}
                      </div>

                      <h3 className={`text-base font-semibold mt-1.5 ${done ? "line-through text-muted-foreground decoration-emerald-400" : ""}`}>
                        {step.title}
                      </h3>

                      <p className="text-sm mt-1 leading-relaxed font-medium" style={{ color: done ? "#9CA3AF" : step.color }}>
                        {step.simple}
                      </p>

                      <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                        {step.detail}
                      </p>

                      {!done && (
                        <Button
                          size="sm"
                          className="mt-3 gap-1.5 h-8 text-xs"
                          style={{ backgroundColor: step.color }}
                          onClick={() => onNavigate(step.route)}
                          data-testid={`onboarding-go-${step.id}`}
                        >
                          {step.buttonLabel} <ArrowRight className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Bonus tips */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
          <HelpCircle className="w-3.5 h-3.5" /> Bonus: What else can I do?
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          {BONUS_TIPS.map(tip => (
            <Card
              key={tip.title}
              className="hover:shadow-sm transition-shadow cursor-pointer group"
              onClick={() => onNavigate(tip.route)}
            >
              <CardContent className="p-4">
                <tip.icon className="w-5 h-5 text-primary mb-2" />
                <h3 className="text-xs font-semibold">{tip.title}</h3>
                <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{tip.desc}</p>
                <div className="flex items-center gap-0.5 text-[10px] text-primary font-medium mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  Go there <ChevronRight className="w-2.5 h-2.5" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </motion.div>

      {/* Quick reference */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
      >
        <Card className="bg-muted/40">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold mb-3">How everything connects</h2>
            <div className="space-y-2.5 text-xs text-muted-foreground leading-relaxed">
              <p>
                <strong className="text-foreground">API Keys</strong> unlock providers.
                <strong className="text-foreground"> Providers</strong> have models.
                <strong className="text-foreground"> Models</strong> generate text and images.
              </p>
              <p>
                <strong className="text-foreground">Unified Endpoint</strong> (<code>/v1/chat/completions</code>) accepts any OpenAI-compatible request and routes it to the best available provider with automatic failover.
              </p>
              <p>
                <strong className="text-foreground">Hub API Keys</strong> let external apps use your gateway. Each key has its own INR wallet, rate limits, and provider restrictions.
              </p>
              <p>
                <strong className="text-foreground">Marketplace</strong> lets you add more providers (DeepSeek, Mistral, etc.) in one click.
              </p>
              <p>
                <strong className="text-foreground">Everything is tracked.</strong> Every token, every rupee, every API call. Dashboard shows the totals. Usage History shows the breakdown.
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
